import React, { useState, useEffect, useCallback } from "react";
import { get, post, patch, del } from "../../../helpers/api_helper";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import {
  Card, CardBody, Row, Col, Button, Spinner, Alert, Badge,
  Modal, ModalHeader, ModalBody, ModalFooter,
  Nav, NavItem, NavLink, Form, FormGroup, Label, Input,
} from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import PageHint from "../../../components/Common/PageHint";
import SLOTS from "./slots";

/**
 * Sayfa Metinleri — sitedeki sabit açıklama metinlerinin dil bazlı yönetimi.
 *
 * Her yuva (ör. "Tarifler — başlık altı açıklama") için 5 dilde ayrı metin
 * girilebilir. Bir dil boş bırakılırsa sitede o dilin varsayılan metni
 * görünür (alanda soluk yazıyla gösterilir). Kayıtlar Site_Text tablosunda
 * (Site_Text_Key + Lang_Code) tutulur.
 */
const ODATA_BASE = "https://buharaapi.stechomeyazilim.info";

const LANGS = [
  { code: "tr", label: "Türkçe", flag: "tr" },
  { code: "en", label: "English", flag: "gb" },
  { code: "de", label: "Deutsch", flag: "de" },
  { code: "fr", label: "Français", flag: "fr" },
  { code: "nl", label: "Nederlands", flag: "nl" },
];

const GROUPS = [...new Set(SLOTS.map((s) => s.group))];
const norm = (code) => (code || "").trim().toLowerCase();

export default function SiteTextsPage() {
  document.title = "Sayfa Metinleri | BUHARA - Admin Paneli";

  const [rows, setRows] = useState([]);           // Site_Text kayıtları
  const [loading, setLoading] = useState(false);
  const [notReady, setNotReady] = useState(false);
  const [current, setCurrent] = useState(null);   // düzenlenen yuva
  const [activeLang, setActiveLang] = useState("tr");
  const [draft, setDraft] = useState({});         // { tr: "...", en: "..." }
  const [saving, setSaving] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await get(`${ODATA_BASE}/Site_Text`);
      setRows(res?.value || []);
      setNotReady(false);
    } catch (err) {
      console.error("Site_Text fetch error:", err);
      setRows([]);
      setNotReady(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const rowFor = (key, lang) =>
    rows.find((r) => r.Site_Text_Key === key && norm(r.Lang_Code) === lang);

  const openEdit = (slot) => {
    const init = {};
    LANGS.forEach((l) => { init[l.code] = rowFor(slot.key, l.code)?.Site_Text_Value || ""; });
    setDraft(init);
    setActiveLang("tr");
    setCurrent(slot);
  };

  const close = () => { if (!saving) setCurrent(null); };

  // Değişen dilleri tek seferde kaydeder: dolu → ekle/güncelle, boşaltılmış → sil
  // (silinince sitede varsayılan metin görünür).
  const handleSave = async () => {
    if (!current) return;
    setSaving(true);
    let changed = 0;
    try {
      for (const { code } of LANGS) {
        const value = (draft[code] || "").trim();
        const existing = rowFor(current.key, code);
        const before = (existing?.Site_Text_Value || "").trim();
        if (value === before) continue;
        changed += 1;
        if (existing?.ID && !value) {
          await del(`${ODATA_BASE}/Site_Text(${existing.ID})`);
        } else if (existing?.ID) {
          await patch(`${ODATA_BASE}/Site_Text(${existing.ID})`, { Site_Text_Value: value });
        } else {
          await post(`${ODATA_BASE}/Site_Text`, {
            Site_Text_Key: current.key, Lang_Code: code, Site_Text_Value: value,
          });
        }
      }
      toastr.success(changed ? "Metinler kaydedildi. Sitede en geç 5 dakika içinde görünür." : "Değişiklik yok.");
      setCurrent(null);
      await fetchRows();
    } catch (err) {
      console.error(err);
      const detail = err.response?.data?.error?.message || err.message;
      toastr.error("Kayıt sırasında hata oluştu: " + detail);
      await fetchRows();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Yönetim" breadcrumbItem="Sayfa Metinleri" />
        <PageHint
          what="Sitedeki sayfa başlıklarının altındaki açıklama metinleri bu ekrandan, her dil için ayrı ayrı yönetilir."
          where="Örneğin Tarifler sayfasındaki 'Buhara baharatlarıyla…' açıklaması, Kalite Belgelerimiz sayfasındaki 'Üretimimiz Türk Gıda…' metni ve Bayilerimiz sayfasındaki 'Global İş Ortaklarımız' başlığı."
          note="Bir dil boş bırakılırsa sitede o dilin varsayılan metni görünür. Değişiklikler sitede en geç 5 dakika içinde yansır."
          links={[
            { label: "Tarifler", path: "/yemek-tarifleri" },
            { label: "Kalite Belgelerimiz", path: "/kalite-belgelerimiz" },
            { label: "Bayilerimiz", path: "/bayilerimiz" },
          ]}
        />

        {notReady && (
          <Alert color="warning">
            <strong>Sayfa metinleri servisi henüz aktif değil.</strong><br />
            Backend'de <code>Site_Text</code> tablosu oluşturulup API yeniden yayınlandıktan
            sonra bu ekran kayıt yapabilecek (bkz. <code>BuharaOdata/README_SiteText.md</code>).
            Site bu süre boyunca varsayılan metinleri doğru dilde göstermeye devam eder.
          </Alert>
        )}

        {loading ? (
          <div className="text-center py-5">
            <Spinner color="warning" /> <span className="ms-2">Yükleniyor...</span>
          </div>
        ) : (
          GROUPS.map((group) => (
            <Card key={group}>
              <CardBody>
                <h5 className="mb-3">{group}</h5>
                <Row>
                  {SLOTS.filter((s) => s.group === group).map((slot) => {
                    const custom = LANGS.filter((l) => rowFor(slot.key, l.code)?.Site_Text_Value);
                    const trValue = rowFor(slot.key, "tr")?.Site_Text_Value || slot.defaults.tr;
                    return (
                      <Col key={slot.key} lg={6} className="mb-3">
                        <div className="border rounded p-3 h-100 d-flex flex-column">
                          <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                            <h6 className="mb-0">{slot.label}</h6>
                            {custom.length ? (
                              <Badge color="success" pill>{custom.map((l) => l.code.toUpperCase()).join(" · ")} özel</Badge>
                            ) : (
                              <Badge color="light" className="text-muted" pill>Varsayılan</Badge>
                            )}
                          </div>
                          <p className="text-muted font-size-13 mb-3 flex-grow-1">{trValue}</p>
                          <div>
                            <Button type="button" color="success" size="sm" disabled={notReady} onClick={() => openEdit(slot)}>
                              <i className="bx bx-edit me-1" /> Düzenle
                            </Button>
                          </div>
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              </CardBody>
            </Card>
          ))
        )}

        <Modal isOpen={!!current} toggle={close} size="lg" fade={false}>
          <ModalHeader toggle={close}>
            {current ? `${current.group} — ${current.label}` : ""}
          </ModalHeader>
          <ModalBody>
            {current && (
              <>
                <Nav tabs className="mb-3">
                  {LANGS.map((l) => (
                    <NavItem key={l.code}>
                      <NavLink
                        href="#"
                        className={activeLang === l.code ? "active fw-bold" : ""}
                        onClick={(e) => { e.preventDefault(); setActiveLang(l.code); }}
                      >
                        <img src={`https://flagcdn.com/w20/${l.flag}.png`} alt="" width={18} height={12}
                          style={{ marginRight: 6, borderRadius: 2, objectFit: "cover" }} />
                        {l.label}
                        {(draft[l.code] || "").trim() && (
                          <span className="ms-1 badge bg-success" style={{ fontSize: 9 }}>✓</span>
                        )}
                      </NavLink>
                    </NavItem>
                  ))}
                </Nav>

                <Form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
                  <FormGroup>
                    <Label>{LANGS.find((l) => l.code === activeLang)?.label} metni</Label>
                    <Input
                      type="textarea"
                      rows={5}
                      value={draft[activeLang] || ""}
                      placeholder={current.defaults[activeLang] || ""}
                      onChange={(e) => setDraft((prev) => ({ ...prev, [activeLang]: e.target.value }))}
                    />
                    <div className="form-text">
                      Boş bırakılırsa sitede soluk görünen varsayılan metin kullanılır.
                      Tüm dillerdeki değişiklikler tek seferde kaydedilir.
                    </div>
                  </FormGroup>
                </Form>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button type="button" color="light" onClick={close} disabled={saving}>İptal</Button>
            <Button type="button" color="success" onClick={handleSave} disabled={saving}>
              {saving ? <Spinner size="sm" className="me-1" /> : null}
              Kaydet
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </div>
  );
}
